import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '@/components/DataTable';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useAdminList } from '@/lib/useAdminList';
import { ListFooter } from '@/components/ListFooter';
import { Search } from 'lucide-react';

interface Row {
  id: string;
  name: string;
  slug: string | null;
  sportTypes: string[];
  logoUrl: string | null;
  city: string | null;
  cityId: string | null;
  plan: string;
  maxMembers: number;
  joinMode: string;
  createdAt: string;
  memberCount: number;
  activityCount: number;
}

export function OrgsListPage() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [city, setCity] = useState('');
  const [sport, setSport] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  const {
    rows, total, loaded, isLoading,
    hasNextPage, isFetchingNextPage, fetchNextPage,
  } = useAdminList<Row>(
    ['admin', 'orgs', debounced, city, sport],
    '/api/admin/orgs',
    { q: debounced, city, sport },
  );

  const columns: ColumnDef<Row>[] = [
    {
      accessorKey: 'name',
      header: 'Название',
      cell: ({ row }) => (
        <div className="flex flex-col">
          <span className="text-zinc-100 font-medium">{row.original.name}</span>
          {row.original.slug ? (
            <span className="font-mono text-[11px] text-zinc-500">/{row.original.slug}</span>
          ) : null}
        </div>
      ),
    },
    {
      accessorKey: 'sportTypes',
      header: 'Спорты',
      cell: (c) => (
        <div className="flex flex-wrap gap-1">
          {(c.getValue() as string[]).map((s) => (
            <Badge key={s} variant="secondary">{s}</Badge>
          ))}
        </div>
      ),
    },
    {
      accessorKey: 'city',
      header: 'Город',
      cell: (c) => {
        const v = c.getValue() as string | null;
        return v ? v : <span className="text-zinc-600">—</span>;
      },
    },
    {
      accessorKey: 'memberCount',
      header: 'Участников',
      cell: (c) => <span className="font-mono tabular-nums text-zinc-300">{c.getValue() as number}</span>,
    },
    {
      accessorKey: 'activityCount',
      header: 'Игр',
      cell: (c) => <span className="font-mono tabular-nums text-zinc-300">{c.getValue() as number}</span>,
    },
    {
      accessorKey: 'createdAt',
      header: 'Создано',
      cell: (c) => (
        <span className="font-mono text-xs tabular-nums text-zinc-500">
          {new Date(c.getValue() as string).toLocaleDateString('ru')}
        </span>
      ),
    },
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-baseline gap-3">
          <h1 className="text-2xl font-semibold tracking-[-0.01em] text-zinc-50">Клубы</h1>
          <span className="text-xs text-zinc-500">
            Найдено: <span className="font-mono tabular-nums text-zinc-300">{total}</span>
          </span>
        </div>
      </div>

      <div className="rounded-lg border border-zinc-800/80 bg-bg-2 p-3 flex gap-3 items-center flex-wrap">
        <div className="relative flex-1 min-w-0 sm:min-w-[16rem] max-w-md">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-zinc-500" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Поиск по названию…"
            className="pl-9"
          />
        </div>
        <Input
          value={city}
          onChange={(e) => setCity(e.target.value)}
          placeholder="город (e.g. Тольятти)"
          className="w-full sm:max-w-[14rem]"
        />
        <Input
          value={sport}
          onChange={(e) => setSport(e.target.value)}
          placeholder="sport (e.g. volleyball)"
          className="w-full sm:max-w-[14rem]"
        />
      </div>

      {isLoading ? (
        <div className="text-zinc-500 text-sm">Загрузка…</div>
      ) : (
        <>
          <DataTable
            columns={columns}
            data={rows}
            onRowClick={(r) => navigate({ to: '/orgs/$id', params: { id: r.id } })}
            empty="Клубов не найдено"
          />
          <ListFooter
            loaded={loaded}
            total={total}
            hasMore={!!hasNextPage}
            isLoading={isFetchingNextPage}
            onMore={() => void fetchNextPage()}
          />
        </>
      )}
    </div>
  );
}
